import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlass,
  ArrowLeft,
  BookOpen,
  Clock,
  User,
  Sparkle,
  HourglassMedium,
} from '@phosphor-icons/react';
import NewsletterForm from '../components/NewsletterForm';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 9;

// Elegant placeholders shown when we have fewer real articles than
// needed to fill a professional-looking grid. Keeps the page rich even
// while the Knowledge Center library is still growing.
const COMING_SOON = [
  { id: 'cs-play', title: 'التعلم القائم على اللعب', category: 'المناهج والاتجاهات التربوية', accent: '#4B8B5F' },
  { id: 'cs-waldorf', title: 'نهج والدورف في الطفولة المبكرة', category: 'المناهج والاتجاهات التربوية', accent: '#B08A50' },
  { id: 'cs-highscope', title: 'نموذج هاي‑سكوب: التخطيط–التنفيذ–المراجعة', category: 'المناهج والاتجاهات التربوية', accent: '#7A5C2B' },
  { id: 'cs-forest', title: 'الغابة التعليمية: التعلم في الطبيعة', category: 'البيئة التعليمية', accent: '#3D512D' },
  { id: 'cs-observation', title: 'مهارة الملاحظة الصفّية: أداة المعلمة الأولى', category: 'أدوات وممارسات مهنية', accent: '#8B6F3E' },
  { id: 'cs-family', title: 'الشراكة مع الأسرة: من الإبلاغ إلى الحوار', category: 'الأسرة والطفل', accent: '#4A5D23' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const KnowledgeCenterPage = () => {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(params.get('q') || '');
  const [activeCat, setActiveCat] = useState(params.get('category') || '');
  const [loading, setLoading] = useState(true);

  // Fetch categories + featured once
  useEffect(() => {
    (async () => {
      try {
        const [c, f] = await Promise.all([
          fetch(`${API}/api/knowledge/categories`).then((r) => r.json()),
          fetch(`${API}/api/knowledge/articles/featured`).then((r) => r.json()),
        ]);
        setCategories(c.categories || []);
        setFeatured(f.article || null);
      } catch {
        /* silent */
      }
    })();
  }, []);

  // Fetch articles whenever filters change
  useEffect(() => {
    setLoading(true);
    const url = new URL(`${API}/api/knowledge/articles`);
    url.searchParams.set('page', page);
    url.searchParams.set('page_size', PAGE_SIZE);
    if (activeCat) url.searchParams.set('category', activeCat);
    if (q.trim()) url.searchParams.set('q', q.trim());

    fetch(url.toString())
      .then((r) => r.json())
      .then((data) => {
        if (page === 1) setArticles(data.items || []);
        else setArticles((prev) => [...prev, ...(data.items || [])]);
        setTotal(data.total || 0);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [page, activeCat, q]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setArticles([]);
    const next = new URLSearchParams(params);
    if (q.trim()) next.set('q', q.trim());
    else next.delete('q');
    setParams(next);
  };

  const handleCategory = (slug) => {
    setActiveCat(slug);
    setPage(1);
    setArticles([]);
    const next = new URLSearchParams(params);
    if (slug) next.set('category', slug);
    else next.delete('category');
    setParams(next);
  };

  // Filter out the featured article from the grid when it's shown
  // at the top of the page, so the same article never appears twice.
  const featuredShown = Boolean(featured && !q && !activeCat);
  const gridArticles = useMemo(() => {
    if (!featuredShown) return articles;
    return articles.filter((a) => a.slug !== featured?.slug);
  }, [articles, featured, featuredShown]);

  // If we have fewer real articles than needed for a rich-looking grid
  // (min 6 cards visible), fill the remainder with "Coming Soon"
  // placeholders — but only on the default browse view (no search /
  // category filter active), so filtered/searched results always show
  // real matches only.
  //
  // NOTE: temporarily hidden from public visitors until the pending
  // articles are ready for publication (per user request). Draft
  // articles remain untouched in the database. To restore, revert
  // this useMemo body to the original logic.
  const placeholders = useMemo(() => [], []);

  const hasMore = articles.length < total;

  return (
    <main
      data-testid="knowledge-center-page"
      dir="rtl"
      className="bg-[#FAFAF7] min-h-screen"
    >
      {/* Section header */}
      <section className="pt-32 sm:pt-36 pb-12 bg-[#F5F2EC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block text-[10px] sm:text-[11px] font-medium tracking-[0.32em] text-[#8B6F3E] uppercase mb-6">
            Knowledge Center
          </span>
          <h1 className="font-['Noto_Kufi_Arabic'] font-extrabold text-[#1F3D2B] text-[32px] sm:text-[44px] md:text-[52px] leading-[1.25] tracking-[-0.01em] mb-6">
            مركز المعرفة
          </h1>
          <div className="w-14 h-px bg-[#B08A50] mx-auto mb-6" />
          <p className="text-[#3D512D] text-[15px] sm:text-base leading-8 max-w-2xl mx-auto">
            مساحة معرفية متخصصة في الطفولة المبكرة — مقالات، أبحاث، وأدوات
            مهنية موجّهة للقيادات والمعلمات والأسر.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        {/* Search + categories */}
        <div className="mb-10 sm:mb-12">
          <form
            onSubmit={handleSearch}
            className="relative max-w-xl mx-auto mb-8"
            data-testid="kc-search-form"
          >
            <MagnifyingGlass
              size={18}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A866E]"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث في المقالات..."
              data-testid="kc-search-input"
              className="w-full bg-white border border-[#E6E0D6] rounded-full py-3 pe-12 ps-5 text-sm text-[#1F3D2B] placeholder-[#7A866E] focus:outline-none focus:border-[#4A641F] transition-colors"
            />
          </form>

          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <button
              type="button"
              data-testid="kc-cat-all"
              onClick={() => handleCategory('')}
              className={`px-4 py-1.5 rounded-full text-[13px] border transition-colors ${
                !activeCat
                  ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                  : 'bg-white text-[#3D512D] border-[#E6E0D6] hover:border-[#4A641F]'
              }`}
            >
              الكل
            </button>
            {categories.map((c) => (
              <button
                type="button"
                key={c.id}
                data-testid={`kc-cat-${c.slug}`}
                onClick={() => handleCategory(c.slug)}
                className={`px-4 py-1.5 rounded-full text-[13px] border transition-colors ${
                  activeCat === c.slug
                    ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                    : 'bg-white text-[#3D512D] border-[#E6E0D6] hover:border-[#4A641F]'
                }`}
              >
                {c.name_ar}
              </button>
            ))}
          </div>
        </div>

        {/* Featured — hero article */}
        {featured && !q && !activeCat && (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="kc-featured"
            className="mb-14 bg-white rounded-[24px] border border-[#E6E0D6] overflow-hidden grid md:grid-cols-2 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="aspect-[4/3] md:aspect-auto bg-[#EEE8DC]">
              {featured.hero_image_url ? (
                <img
                  src={featured.hero_image_url}
                  alt={featured.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#B08A50]">
                  <BookOpen size={64} weight="duotone" />
                </div>
              )}
            </div>
            <div className="p-8 sm:p-10 flex flex-col justify-center">
              <span className="inline-block text-[10px] font-medium tracking-[0.24em] text-[#8B6F3E] uppercase mb-3">
                Featured
              </span>
              {featured.category_name_ar && (
                <p className="text-[12px] text-[#4A5D23] mb-3">
                  {featured.category_name_ar}
                </p>
              )}
              <h2 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[24px] sm:text-[28px] leading-tight mb-4">
                {featured.title}
              </h2>
              <p className="text-[#6B7565] leading-8 mb-6">{featured.excerpt}</p>
              <div className="flex items-center gap-4 text-[12px] text-[#7A866E] mb-6">
                <span className="flex items-center gap-1.5">
                  <Clock size={13} /> {featured.reading_time_min} د.
                </span>
                <span>{fmtDate(featured.published_at)}</span>
              </div>
              <Link
                to={`/knowledge/${featured.slug}`}
                data-testid="kc-featured-read-btn"
                className="inline-flex items-center gap-2 self-start bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-6 py-3 text-sm font-bold transition-colors"
              >
                قراءة المقال
                <ArrowLeft size={14} className="rtl-flip" />
              </Link>
            </div>
          </motion.article>
        )}

        {/* Grid */}
        {articles.length === 0 && !loading && (
          <div
            data-testid="kc-empty-state"
            className="text-center py-24 text-[#7A866E]"
          >
            <BookOpen
              size={48}
              weight="duotone"
              className="mx-auto text-[#B08A50] mb-4"
            />
            <p className="text-base">لم تُنشر أي مقالات بعد</p>
            <p className="text-sm mt-1">تابعنا قريباً — نُعدّ محتوى مرجعياً متخصصاً</p>
          </div>
        )}

        {(gridArticles.length > 0 || placeholders.length > 0) && (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="kc-grid"
          >
            {gridArticles.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                data-testid={`kc-card-${a.slug}`}
                className="bg-white rounded-[20px] border border-[#E6E0D6] overflow-hidden flex flex-col hover:shadow-md transition-shadow"
              >
                <Link to={`/knowledge/${a.slug}`} className="aspect-[4/3] bg-[#EEE8DC] block overflow-hidden">
                  {a.hero_image_url ? (
                    <img
                      src={a.hero_image_url}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#B08A50]">
                      <BookOpen size={40} weight="duotone" />
                    </div>
                  )}
                </Link>
                <div className="p-6 flex-1 flex flex-col">
                  {a.category_name_ar && (
                    <p className="text-[11px] text-[#4A5D23] tracking-wide mb-2">
                      {a.category_name_ar}
                    </p>
                  )}
                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[17px] leading-snug mb-3">
                    <Link to={`/knowledge/${a.slug}`} className="hover:text-[#4A641F] transition-colors">
                      {a.title}
                    </Link>
                  </h3>
                  {a.excerpt && (
                    <p className="text-sm text-[#6B7565] leading-7 mb-4 line-clamp-3">
                      {a.excerpt}
                    </p>
                  )}
                  <div className="mt-auto pt-4 border-t border-[#F0EBE2] flex items-center justify-between text-[11px] text-[#7A866E]">
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {a.reading_time_min} د.
                    </span>
                    <Link
                      to={`/knowledge/${a.slug}`}
                      data-testid={`kc-read-btn-${a.slug}`}
                      className="inline-flex items-center gap-1 text-[#1F3D2B] font-bold border-b border-[#B08A50] hover:border-[#1F3D2B] pb-0.5 transition-colors"
                    >
                      قراءة المقال
                      <ArrowLeft size={12} className="rtl-flip" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}

            {placeholders.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (gridArticles.length + i) * 0.05, duration: 0.5 }}
                data-testid={`kc-placeholder-${p.id}`}
                aria-label={`قريباً — ${p.title}`}
                className="relative rounded-[20px] border border-dashed border-[#D6CBB2] bg-gradient-to-br from-[#FDFBF6] to-[#F5F2EC] overflow-hidden flex flex-col group cursor-default"
              >
                <div
                  className="aspect-[4/3] flex items-center justify-center relative overflow-hidden"
                  style={{
                    background:
                      `radial-gradient(circle at 30% 30%, ${p.accent}18 0%, transparent 55%),` +
                      `radial-gradient(circle at 70% 75%, ${p.accent}12 0%, transparent 60%),` +
                      '#F5F2EC',
                  }}
                >
                  <div className="text-center px-6">
                    <div
                      className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
                      style={{ background: `${p.accent}22`, color: p.accent }}
                    >
                      <HourglassMedium size={22} weight="duotone" />
                    </div>
                    <div
                      className="text-[10.5px] font-semibold tracking-[0.2em] uppercase"
                      style={{ color: p.accent }}
                    >
                      Coming Soon
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 opacity-40 group-hover:opacity-70 transition-opacity">
                    <Sparkle size={14} weight="duotone" style={{ color: p.accent }} />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-[11px] text-[#8B6F3E] tracking-wide mb-2">
                    {p.category}
                  </p>
                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#3D512D] text-[17px] leading-snug mb-3">
                    قريباً — {p.title}
                  </h3>
                  <p className="text-sm text-[#8B937F] leading-7 mb-4 line-clamp-3">
                    مقال قيد التحرير ضمن سلسلة المناهج والممارسات في مركز المعرفة. تابعنا ليصلك عند نشره.
                  </p>
                  <div className="mt-auto pt-4 border-t border-dashed border-[#E6E0D6] flex items-center justify-between text-[11px] text-[#B0A99B]">
                    <span className="italic">في الطريق إليك</span>
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: p.accent }}>
                      2026
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="text-center mt-12">
            <button
              type="button"
              data-testid="kc-load-more-btn"
              onClick={() => setPage((p) => p + 1)}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white rounded-xl px-8 py-3 text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'جارٍ التحميل...' : 'عرض المزيد'}
            </button>
          </div>
        )}

        {/* Newsletter subscription */}
        <section className="mt-20">
          <div className="max-w-3xl mx-auto bg-white border border-[#E6E0D6] rounded-[24px] p-8 sm:p-10 shadow-sm">
            <NewsletterForm
              variant="light"
              source="knowledge"
              title="اشترك في نشرة مركز المعرفة"
              subtitle="ابقَ على تواصل مع مقالاتنا الجديدة، الأدوات المهنية، ومستجدات الطفولة المبكرة — رسالة واحدة شهريًا، بدون إزعاج."
            />
          </div>
        </section>
      </div>
    </main>
  );
};

export default KnowledgeCenterPage;
